import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload, Lightning, ClipboardText, Truck, ShieldCheck, Warning } from "@phosphor-icons/react";

const TABS = [
  { key: "prs", label: "Purchase Requisitions", icon: ClipboardText, testId: "tab-prs" },
  { key: "pos", label: "Purchase Orders", icon: Truck, testId: "tab-pos" },
  { key: "grns", label: "GRN", icon: ShieldCheck, testId: "tab-grns" },
  { key: "ncrs", label: "NCR / CAPA", icon: Warning, testId: "tab-ncrs" },
  { key: "ocr", label: "Invoice OCR", icon: Lightning, testId: "tab-ocr" },
];

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  approved: "bg-sky-50 text-sky-700",
  converted: "bg-emerald-50 text-emerald-700",
  open: "bg-sky-50 text-sky-700",
  partial: "bg-amber-50 text-amber-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
  pending: "bg-amber-50 text-amber-700",
  accepted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
  not_required: "bg-slate-100 text-slate-600",
  closed: "bg-emerald-50 text-emerald-700",
};

export default function Procurement() {
  const [tab, setTab] = useState("prs");
  const [data, setData] = useState({ prs: [], pos: [], grns: [], ncrs: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [prs, pos, grns, ncrs] = await Promise.all([
        api.get("/inventory/prs"),
        api.get("/inventory/pos"),
        api.get("/inventory/grns"),
        api.get("/inventory/ncrs"),
      ]);
      setData({ prs: prs.data, pos: pos.data, grns: grns.data, ncrs: ncrs.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const autoFromLow = async () => {
    try {
      const { data: pr } = await api.post("/inventory/prs/from-low-stock");
      toast.success(`${pr.code} created with ${pr.lines.length} lines`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No low stock items");
    }
  };

  const approvePR = async (id) => {
    try {
      await api.post(`/inventory/prs/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="procurement-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
            Inventory · Procurement & QC
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Procurement Desk</h1>
          <p className="text-sm text-slate-500 mt-1">
            From requisition to receipt to QC — one flow, full ISO 22000 traceability.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={autoFromLow}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="auto-pr-btn"
          >
            <Lightning size={14} weight="bold" /> Auto-PR from low stock
          </button>
        </div>
      </div>

      <div className="border-b border-slate-200 flex items-center gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              data-testid={t.testId}
            >
              <Icon size={14} weight={active ? "fill" : "bold"} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "prs" && <PRTable rows={data.prs} onApprove={approvePR} loading={loading} />}
      {tab === "pos" && <POTable rows={data.pos} loading={loading} />}
      {tab === "grns" && <GRNTable rows={data.grns} onChanged={load} loading={loading} />}
      {tab === "ncrs" && <NCRTable rows={data.ncrs} onChanged={load} loading={loading} />}
      {tab === "ocr" && <InvoiceOCR />}
    </div>
  );
}

function Empty({ children }) {
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-lg px-6 py-12 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}

function StatusChip({ value }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${STATUS_TONE[value] || "bg-slate-100 text-slate-600"}`}>
      {(value || "").replace("_", " ") || "—"}
    </span>
  );
}

function PRTable({ rows, onApprove, loading }) {
  if (!loading && rows.length === 0) return <Empty>No purchase requisitions yet. Use &quot;Auto-PR from low stock&quot; to seed one.</Empty>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Title", "Dept", "Lines", "Status", "Created", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`pr-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.title}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.department}</td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.lines || []).length}</td>
              <td className="px-4 py-3"><StatusChip value={r.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-right">
                {r.status === "draft" && (
                  <button onClick={() => onApprove(r.id)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`pr-approve-${r.id}`}>Approve →</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function POTable({ rows, loading }) {
  if (!loading && rows.length === 0) return <Empty>No purchase orders yet. Approve a PR and convert it to a PO via API/POST.</Empty>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Vendor", "Lines", "Total", "Status", "ETA"].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`po-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.vendor_name}</td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.lines || []).length}</td>
              <td className="px-4 py-3 text-sm tabular-nums">₹{Number(r.total).toLocaleString("en-IN")}</td>
              <td className="px-4 py-3"><StatusChip value={r.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.expected_delivery || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GRNTable({ rows, onChanged, loading }) {
  if (!loading && rows.length === 0) return <Empty>No GRNs yet. Receive against a PO via the API to create one.</Empty>;
  const decide = async (grnId, decision) => {
    const note = window.prompt(`Inspector notes for ${decision}:`, decision === "reject" ? "Failed visual / lab check" : "Within spec");
    if (note === null) return;
    try {
      await api.post(`/inventory/grns/${grnId}/qc`, { decision, sample_size: 5, inspector_notes: note });
      toast.success(`QC ${decision}ed`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "PO", "Vendor", "Lines", "QC Status", "Received", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`grn-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.po_code}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.vendor_name}</td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.lines || []).length}</td>
              <td className="px-4 py-3"><StatusChip value={r.qc_status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.received_at}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                {r.qc_status === "pending" && (
                  <>
                    <button onClick={() => decide(r.id, "accept")} className="text-xs font-medium text-emerald-700 hover:text-emerald-900 mr-3" data-testid={`grn-accept-${r.id}`}>Accept</button>
                    <button onClick={() => decide(r.id, "reject")} className="text-xs font-medium text-rose-700 hover:text-rose-900" data-testid={`grn-reject-${r.id}`}>Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NCRTable({ rows, onChanged, loading }) {
  if (!loading && rows.length === 0) return <Empty>No NCRs raised. NCRs are auto-created when a GRN fails QC.</Empty>;
  const close = async (id) => {
    if (!window.confirm("Close this NCR? All CAPA actions should be completed first.")) return;
    try {
      await api.post(`/inventory/ncrs/${id}/close`);
      toast.success("NCR closed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Code", "Title", "Vendor", "CAPA", "Status", "Raised", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`ncr-row-${r.id}`}>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{r.code}</td>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.title}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{r.vendor_name}</td>
              <td className="px-4 py-3 text-sm tabular-nums">{(r.capa_actions || []).length}</td>
              <td className="px-4 py-3"><StatusChip value={r.status} /></td>
              <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-right">
                {r.status === "open" && (
                  <button onClick={() => close(r.id)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`ncr-close-${r.id}`}>Close</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceOCR() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [model, setModel] = useState("");

  const onPick = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/inventory/invoices/ocr", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.parsed);
      setModel(data.model);
      toast.success("Invoice parsed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-5" data-testid="ocr-tab">
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
            <Lightning size={22} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">Vendor Invoice OCR</h3>
            <p className="text-sm text-slate-500 mt-1">
              Drop a PDF / PNG / JPG and we&apos;ll extract vendor, invoice no, date, GSTIN, line items and totals using Gemini 3 Pro. Use this to bootstrap GRN data without typing.
            </p>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid="ocr-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              data-testid="ocr-pick-btn"
            >
              <Upload size={14} weight="bold" /> {busy ? "Reading invoice…" : "Upload invoice"}
            </button>
          </div>
        </div>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ocr-result">
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold">Extracted invoice</div>
            <div className="text-[11px] text-emerald-700">model: {model}</div>
          </div>
          <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Vendor" value={result.vendor_name} />
            <Field label="Invoice No" value={result.invoice_number} />
            <Field label="Date" value={result.invoice_date} />
            <Field label="GSTIN" value={result.gstin} />
          </div>
          <table className="w-full">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr>{["#", "Description", "HSN", "Qty", "UoM", "Unit ₹", "Line ₹"].map((h) => <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(result.line_items || []).map((li, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-xs text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 text-sm font-medium text-slate-900">{li.description}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono">{li.hsn}</td>
                  <td className="px-4 py-2 text-sm tabular-nums">{li.qty}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{li.uom}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">{fmtINR(li.unit_price)}</td>
                  <td className="px-4 py-2 text-xs text-slate-700 tabular-nums">{fmtINR(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-6 text-sm">
            <div><span className="text-slate-500">Subtotal:</span> <span className="font-medium tabular-nums">{fmtINR(result.subtotal)}</span></div>
            <div><span className="text-slate-500">GST:</span> <span className="font-medium tabular-nums">{fmtINR(result.gst_amount)}</span></div>
            <div className="text-base"><span className="text-slate-500">Total:</span> <span className="font-bold tabular-nums">{fmtINR(result.total)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-0.5 truncate" title={value}>{value || "—"}</div>
    </div>
  );
}
